import { useEffect, useState } from "react";
import { Loader, ActionIcon, Tooltip, Text, Paper, Group } from "@mantine/core";
import { DataTable, ConfirmModal, Button, Alert, Badge } from "@xplatform/design-system";
import type { DataTableColumn } from "@xplatform/design-system";
import {
    IconPlus,
    IconTrash,
    IconToggleLeft,
    IconToggleRight,
    IconCloud,
    IconKey,
    IconBrain,
} from "@tabler/icons-react";
import { useProvider } from "@hooks/useProvider";
import type { ProviderViewModel } from "@models/provider";
import { CreateProviderDialog } from "./CreateProviderDialog";
import { CredentialDrawer } from "@features/Provider/CredentialDrawer";
import { ModelModal } from "@src/features/Provider/ModelModal";
import { formatShortDate as formatDate } from "@utils/formatters";
import "./ProviderManagementPage.scss";

export const ProviderManagementPage = () => {
    const {
        providers,
        models,
        credentials,
        isLoading,
        error,
        fetchProviders,
        fetchModels,
        fetchCredentials,
        createProvider,
        updateProvider,
        deleteProvider,
        createCredential,
        updateCredential,
        deleteCredential,
    } = useProvider();

    const [createOpen, setCreateOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deletingProvider, setDeletingProvider] =
        useState<ProviderViewModel | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const [credentialDrawer, setCredentialDrawer] =
        useState<ProviderViewModel | null>(null);
    const [modelDrawer, setModelDrawer] = useState<ProviderViewModel | null>(
        null,
    );

    useEffect(() => {
        fetchProviders();
    }, [fetchProviders]);

    const openCredentials = (provider: ProviderViewModel) => {
        fetchCredentials(provider.id);
        setCredentialDrawer(provider);
    };

    const openModels = (provider: ProviderViewModel) => {
        fetchModels(provider.id);
        setModelDrawer(provider);
    };

    const handleToggleStatus = async (provider: ProviderViewModel) => {
        const newStatus = provider.status === "active" ? "inactive" : "active";
        await updateProvider(provider.id, { status: newStatus });
    };

    const handleDeleteClick = (provider: ProviderViewModel) => {
        setDeletingProvider(provider);
        setDeleteError(null);
        setDeleteOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingProvider) return;
        try {
            await deleteProvider(deletingProvider.id);
            setDeleteOpen(false);
            setDeletingProvider(null);
        } catch (err) {
            setDeleteError(err instanceof Error ? err.message : "Failed to delete");
        }
    };

    const columns: DataTableColumn<ProviderViewModel>[] = [
        {
            key: "key",
            header: "Key",
            width: "28%",
            render: (provider) => (
                <Text size="sm" fw={500}>
                    {provider.key}
                </Text>
            ),
        },
        {
            key: "type",
            header: "Type",
            width: "16%",
            render: (provider) => (
                <Badge variant="outline" size="sm">
                    {provider.type}
                </Badge>
            ),
        },
        {
            key: "status",
            header: "Status",
            width: "12%",
            render: (provider) => (
                <Badge
                    size="sm"
                    color={provider.status === "active" ? "green" : "gray"}
                >
                    {provider.status}
                </Badge>
            ),
        },
        {
            key: "created",
            header: "Created",
            width: "20%",
            render: (provider) => (
                <Text size="sm" c="dimmed">
                    {formatDate(provider.createdAt)}
                </Text>
            ),
        },
        {
            key: "actions",
            header: "Actions",
            width: "24%",
            align: "right",
            render: (provider) => (
                <Group gap={4} justify="flex-end">
                    <Tooltip label="Credentials">
                        <ActionIcon
                            variant="subtle"
                            color="blue"
                            size="sm"
                            onClick={() => openCredentials(provider)}
                        >
                            <IconKey size={16} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Models">
                        <ActionIcon
                            variant="subtle"
                            color="violet"
                            size="sm"
                            onClick={() => openModels(provider)}
                        >
                            <IconBrain size={16} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip
                        label={
                            provider.status === "active" ? "Deactivate" : "Activate"
                        }
                    >
                        <ActionIcon
                            variant="subtle"
                            color={provider.status === "active" ? "yellow" : "green"}
                            size="sm"
                            onClick={() => handleToggleStatus(provider)}
                        >
                            {provider.status === "active" ? (
                                <IconToggleRight size={16} />
                            ) : (
                                <IconToggleLeft size={16} />
                            )}
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Delete">
                        <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            onClick={() => handleDeleteClick(provider)}
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            ),
        },
    ];

    return (
        <div className="admin-page-content">
            <Group justify="space-between" align="center" mb="lg">
                <Text component="h1" size="xl" fw={700}>
                    Providers
                </Text>
                <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => setCreateOpen(true)}
                >
                    Add Provider
                </Button>
            </Group>

            {error && (
                <Alert color="red" mb="sm">
                    {error}
                </Alert>
            )}

            {isLoading && providers.length === 0 ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "2rem 0",
                    }}
                >
                    <Loader />
                </div>
            ) : providers.length === 0 ? (
                <div className="provider-page__empty-state">
                    <IconCloud size={48} color="gray" />
                    <Text size="lg" c="dimmed" mb="xs">
                        No providers configured
                    </Text>
                    <Text size="sm" c="dimmed">
                        Add a provider to get started
                    </Text>
                </div>
            ) : (
                <Paper className="provider-page__table-container">
                    <DataTable<ProviderViewModel>
                        className="provider-page__table"
                        fz="sm"
                        verticalSpacing="xs"
                        horizontalSpacing="md"
                        columns={columns}
                        data={providers}
                        getRowKey={(provider) => provider.id}
                    />
                </Paper>
            )}

            <CreateProviderDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSuccess={fetchProviders}
                onCreate={createProvider}
            />

            <ConfirmModal
                opened={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDeleteConfirm}
                title="Delete Provider"
                confirmLabel="Delete"
                cancelLabel="Cancel"
                danger
                loading={isLoading}
                size="xs"
                portalProps={{ target: "#admin-portal-root" }}
                message={
                    <>
                        {deleteError && (
                            <Alert color="red" mb="sm">
                                {deleteError}
                            </Alert>
                        )}
                        <Text size="sm">
                            Are you sure you want to delete provider{" "}
                            <strong>{deletingProvider?.key}</strong>? This will also delete
                            all associated models and credentials.
                        </Text>
                    </>
                }
            />

            {credentialDrawer && (
                <CredentialDrawer
                    opened={!!credentialDrawer}
                    onClose={() => setCredentialDrawer(null)}
                    providerKey={credentialDrawer.key}
                    credentials={credentials.get(credentialDrawer.id) ?? []}
                    onAdd={(data) => createCredential(credentialDrawer.id, data)}
                    onUpdate={(id, data) =>
                        updateCredential(credentialDrawer.id, id, data)
                    }
                    onDelete={(id) => deleteCredential(credentialDrawer.id, id)}
                />
            )}

            {modelDrawer && (
                <ModelModal
                    opened={!!modelDrawer}
                    onClose={() => setModelDrawer(null)}
                    providerId={modelDrawer.id}
                    providerKey={modelDrawer.key}
                    models={models.get(modelDrawer.id) ?? []}
                    onRefresh={() => fetchModels(modelDrawer.id)}
                />
            )}
        </div>
    );
};
